"use client";

import React from "react";
import type {
  SearchProduct,
  SearchCategory,
  SearchBrand,
  SearchResults,
} from "./HeaderTypes";

interface SearchDropdownContentProps {
  searchTerm: string;
  isFetching: boolean;
  searchResults: SearchResults | undefined;
  mostWanted: any[];
  recentSearches: string[];
  onTermClick: (term: string, item?: any) => void;
  onResultClick: (path: string, term: string) => void;
}

const SearchDropdownContent: React.FC<SearchDropdownContentProps> = ({
  searchTerm,
  isFetching,
  searchResults,
  mostWanted,
  recentSearches,
  onTermClick,
  onResultClick,
}) => {
  return (
    <>
      {!searchTerm && (
        <div className="py-2">
          <div>
            <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
              Popular searches
            </p>
            {mostWanted?.slice(0, 6)?.map((s: any) => (
              <button
                key={s._id || s.productName}
                className="w-full text-left px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                onClick={() => onTermClick(s?.productName, s)}
              >
                <span className="text-sm text-gray-800">{s?.productName}</span>
              </button>
            ))}
          </div>
          {recentSearches.length > 0 && (
            <div className="mt-2">
              <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                Recent searches
              </p>
              {recentSearches.map((s) => (
                <button
                  key={`recent-${s}`}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                  onClick={() => onTermClick(s)}
                >
                  <span className="text-sm text-gray-800">{s}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {searchTerm && isFetching && (
        <div className="p-4 text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500 mx-auto"></div>
          <p className="text-sm text-gray-500 mt-2">Searching...</p>
        </div>
      )}

      {searchTerm && !isFetching && searchResults && (
        <div className="py-2">
          {/* Products */}
          {searchResults.enrichedProducts &&
            searchResults.enrichedProducts.length > 0 && (() => {
              const normalize = (str: string) => str.toLowerCase().replace(/["'`~!@#$%^&*()_=+[{\]}\\|;:,.<>/?]/g, " ").replace(/\s+/g, " ").trim();
              const term = normalize(searchTerm);
              const termWords = term.split(" ").filter(Boolean);
              const exactMatches = searchResults.enrichedProducts.filter((p: SearchProduct) => {
                const name = normalize(p.productName || "");
                // Score: count how many search words appear in product name
                const matchCount = termWords.filter((w) => name.includes(w)).length;
                // Accept if at least 40% of words match
                return matchCount >= Math.ceil(termWords.length * 0.4);
              }).sort((a: SearchProduct, b: SearchProduct) => {
                // Sort by match score (most matching words first)
                const nameA = normalize(a.productName || "");
                const nameB = normalize(b.productName || "");
                const scoreA = termWords.filter((w) => nameA.includes(w)).length;
                const scoreB = termWords.filter((w) => nameB.includes(w)).length;
                return scoreB - scoreA;
              });
              const displayProducts = exactMatches.length > 0
                ? exactMatches
                : searchResults.enrichedProducts;

              return displayProducts.length > 0 && (
                <div>
                  <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                    Products
                  </p>
                  {displayProducts.slice(0, 10).map((p: SearchProduct) => (
                    <div
                      key={p._id}
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                      onClick={() =>
                        onResultClick(`/product/${p.urlSlug}`, p.productName)
                      }
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex gap-2 items-center min-w-0">
                          {p.productImages && p.productImages.length > 0 && (
                            <img
                              src={p.productImages[0]}
                              alt={p.productName}
                              className="object-cover w-8 h-8 rounded flex-shrink-0"
                              loading="lazy"
                            />
                          )}
                          <p className="text-sm font-medium text-gray-900 truncate min-w-0 w-[120px] sm:w-[180px] md:w-[220px] lg:w-[280px]">
                            {p.productName}
                          </p>
                        </div>
                        <p className="text-sm font-semibold text-red-600 ml-2 whitespace-nowrap">
                          {/* ₹{p.sellingPrice} */}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

          {/* Categories */}
          {searchResults.categories && searchResults.categories.length > 0 && (
            <div>
              <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                Categories
              </p>
              {searchResults.categories.map((c: SearchCategory) => (
                <div
                  key={c._id}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                  onClick={() => onResultClick(`/categories/${c._id}`, c.name)}
                >
                  <div className="flex items-center gap-3">
                    {c.image && (
                      <img
                        src={c.image}
                        alt={c.name}
                        className="w-8 h-8 object-cover rounded"
                        loading="lazy"
                      />
                    )}
                    <p className="text-sm font-medium text-gray-900">
                      {c.name}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Brands */}
          {searchResults.brands && searchResults.brands.length > 0 && (
            <div>
              <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                Brands
              </p>
              {searchResults.brands.map((b: SearchBrand) => (
                <div
                  key={b._id}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
                  onClick={() => onResultClick(`/brands/${b._id}`, b.brandName)}
                >
                  <div className="flex items-center gap-3">
                    {b.imageUrl && (
                      <img
                        src={b.imageUrl}
                        alt={b.brandName}
                        className="w-8 h-8 object-cover rounded"
                        loading="lazy"
                      />
                    )}
                    <p className="text-sm font-medium text-gray-900">
                      {b.brandName}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Results */}
          {!searchResults?.enrichedProducts?.length &&
            !searchResults?.categories?.length &&
            !searchResults?.brands?.length && (
              <div className="p-4 text-center">
                <p className="text-sm text-gray-500">
                  No results found for "{searchTerm}"
                </p>
              </div>
            )}
        </div>
      )}
    </>
  );
};

export default SearchDropdownContent;
